// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    cards = "cards",
}

const enum bucketCardFields {
    image = "image",
    title = "title",
    description = "description",
}

interface BucketCardModel {
    fields: {
        image: string;
        title: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: BucketCardModel[];
    };
}

type Props = OwnProps;

export interface BucketCardsStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

export const bucketCardsStyles: BucketCardsStyles = {};

const styles = bucketCardsStyles;
const BucketCards: FC<Props> = ({ fields: { cards } }) => {
    if (!cards.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="bucket-cards-container">
            {cards.map(({ fields: { image, title, description } }, index) => (
                <StyledWrapper key={`${title}-${index}`} {...styles.wrapper} data-test-selector="bucket-card-wrapper">
                    <LazyImage src={image} altText={`Image Of ${title}`} {...styles.image} />
                    <Typography {...styles.title}>{title}</Typography>
                    <Typography {...styles.description}>{parse(description, xParserOptions)}</Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: BucketCards,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: bucketCardFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: bucketCardFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: bucketCardFields.description,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
